import React, {
  useEffect, useState, useRef, Fragment
} from 'react';
import Select from 'react-select';

import SvgIcon from 'src/react_components/svg-icon';
import renderReact from 'src/utilities/render-react';
import PartnerCarousel from './partner-carousel';
import PartnerList from './partner-list';

type AutocompleteSuggestion = {
  name: string;
  slug: string;
};

type Props = {
  clientsPath: string;
};

type Partner = {
  clientName: string;
  slug: string;
  logo: string | null;
  url: string;
};

type PartnerTypeOption = {
  value: string;
  label: string;
};

const PARTNERS_PER_PAGE = 20;

const PARTNER_TYPES = [
  { value: 'all', label: 'All institution types' },
  { value: 'university', label: 'University' },
  { value: 'tafe', label: 'TAFE/College' },
  { value: 'library', label: 'Library' },
  { value: 'school', label: 'School' }
];

const PARTNER_SLUGS = [
  'la-trobe-university',
  'the-university-of-adelaide',
  'university-of-western-australia',
  'university-of-new-south-wales',
  'the-university-of-exeter',
  'curtin-university',
  'birkbeck-university-of-london',
  'plymouth-marjon-university',
  'charles-sturt-university',
  'sheffield-hallam-university',
  'yorkville-university',
  'uow-dubai',
  'flinders-university',
  'auckland-university-of-technology',
  'james-cook-university',
  'edith-cowan-university',
  'murdoch-university',
  'massey-university',
  'ulster-university',
  'otago-polytechnic',
  'the-university-of-waikato',
  'manchester-metropolitan-university',
  'bond-university-college',
  'the-university-of-sydney'
];

const AccessPage = ({ clientsPath }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [partners, setPartners] = useState<Map<number, Partner[]>>(new Map());
  const [currentPartners, setCurrentPartners] = useState<Partner[]>([]);
  const [currentName, setCurrentName] = useState('');
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [name, setName] = useState('');
  const [hideCarousel, setHideCarousel] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [partnerType, setPartnerType] = useState<PartnerTypeOption>(
    { value: 'all', label: 'All institution types' }
  );
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !dropdownRef?.current?.contains(event.target as Node) &&
        !inputRef?.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (query.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const typeValue = partnerType.value;
      let apiURL = `${clientsPath}?name=${encodeURIComponent(query)}`;
      if (typeValue !== 'all') {
        apiURL += `&client_type=${encodeURIComponent(typeValue)}`;
      }
      apiURL += '&page=1';

      const response = await fetch(apiURL);
      if (!response.ok) return;

      const result = await response.json();
      const clients = result.clients || [];

      setSuggestions(
        clients.slice(0, 10).map((client: { name: string; slug: string }) => ({
          name: client.name,
          slug: client.slug
        }))
      );
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching suggestions:', err);
    }
  };

  const fetchData = async (fromSearch = false, searchName?: string) => {
    try {
      setIsLoading(true);

      if (fromSearch) {
        setHideCarousel(true);
        setCurrentPage(1);
        setPartners(new Map());
      }

      const includeCarouselSlugs = !hideCarousel && !fromSearch;
      const result = await callAPI(includeCarouselSlugs, searchName);

      if (result.length === 0) {
        if (fromSearch) {
          setCurrentPartners([]);
        }
      } else {
        if (partners.has(currentPage) && !fromSearch) {
          setCurrentPartners(partners.get(currentPage) as Partner[]);
          setIsLoading(false);
          return;
        }

        setPartners((prev) => new Map(prev).set(currentPage, result));
        setCurrentPartners(result);
      }
      setIsLoading(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching partners:', err);
    }
  };

  const handleImageLoad = (slug: string) => {
    setLoadedImages((prev) => new Set(prev).add(slug));
  };

  const callAPI = async (includeCarouselSlugs = false, searchName?: string) => {
    const nameToUse = searchName !== undefined ? searchName : name;
    const searchParams = [
      nameToUse && `name=${encodeURIComponent(nameToUse)}`,
      includeCarouselSlugs && PARTNER_SLUGS.map((slug) => `slugs[]=${encodeURIComponent(slug)}`).join('&'),
      partnerType.value === 'all' ? null : `client_type=${encodeURIComponent(partnerType.value)}`,
      `page=${currentPage}`
    ].filter(Boolean);
    const apiURL = `${clientsPath}?${searchParams.join('&')}`;
    const response = await fetch(apiURL);

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const result = await response.json();
    const clients = result.clients || [];
    const total = result.total || 0;
    setTotalPages(Math.ceil(total / PARTNERS_PER_PAGE));
    setTotalCount(total);
    setCurrentName(nameToUse);

    return clients.map((client: { name: string; slug: string; logo: string | null; auth_uri: string }) => {
      let clientName = client.name;

      // Specific name changes per TAFE request
      if (clientName === 'TAFE NSW Sydney Region - North Sydney RTO') {
        clientName = `TAFE NSW Sydney Region – Northern Sydney RTO (Bradfield,
            Hornsby, Meadowbank, St Leonards, Northern Beaches, Ryde)`;
      } else if (clientName === 'TAFE NSW Sydney Region - Sydney RTO') {
        clientName = `TAFE NSW Sydney Region – Sydney RTO
            (Design Centre Enmore, Eora, Petersham, Randwick, St George, Gymea, Loftus, Ultimo)`;
      }

      return client.logo || client.slug !== 'the-open-university-uk' ? {
        clientName,
        slug: client.slug,
        logo: client.logo,
        url: client.auth_uri
      } : null;
    }).filter(Boolean);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value || '';
    setName(newValue);

    if (newValue === '' && name !== '') {
      fetchData(true, newValue);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        fetchData(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          selectSuggestion(suggestions[selectedIndex]);
        } else {
          fetchData(true);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const selectSuggestion = (suggestion: AutocompleteSuggestion) => {
    setName(suggestion.name);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    fetchData(true, suggestion.name);
  };

  return (
    <div className="access-page d-flex flex-column">
      <section className="access-page-header d-flex flex-column gap-4 w-100 text-center">
        <h1 className="mb-0 text-white">
          Find your Studiosity account here
        </h1>

        <h5 className="mb-0 text-white u-fontSize20 u-fontWeight300">
          Follow these steps to find your personal account, only accessible through your institution's student portal
        </h5>
      </section>

      <section className="container m-auto d-flex flex-column">
        <div
          className="search-container d-flex flex-column align-items-center justify-content-center gap-4
            rounded-2 shadow-sm position-relative px-4 py-4 border border-1 border-light bg-white w-100 z-1"
        >
          <h3 className="m-0 text-center">
            Where are you studying?
          </h3>

          <div className="d-flex align-items-center justify-content-center gap-3 w-100 flex-wrap flex-md-nowrap">
            <div
              id="institution-name"
              className="position-relative"
            >
              <input
                ref={inputRef}
                type="search"
                name="institution_name"
                className="form-control"
                autoComplete="off"
                spellCheck="false"
                dir="ltr"
                placeholder="Search by institution name"
                value={name}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="autocomplete-dropdown position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                  style={{ zIndex: 1000 }}
                  role="listbox"
                >
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.slug}
                      className={`autocomplete-item px-3 py-2 cursor-pointer ${
                        index === selectedIndex ? 'active' : ''
                      }`}
                      role="option"
                      aria-selected={index === selectedIndex}
                      tabIndex={0}
                      onClick={() => selectSuggestion(suggestion)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          selectSuggestion(suggestion);
                        }
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      {suggestion.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Select
              id="institution-type"
              classNamePrefix="institution-type"
              options={PARTNER_TYPES}
              value={partnerType}
              placeholder=""
              onChange={(option) => setPartnerType({
                value: option?.value || 'all',
                label: option?.label || 'All institution types'
              })}
              menuPortalTarget={document.body}
            />

            <button
              type="button"
              id="partner-submit"
              className="btn flex-shrink-0 align-items-center justify-content-center gap-2
                text-white rounded-4 px-3 py-2"
              onClick={() => { fetchData(true); }}
            >
              <SvgIcon icon="search" className="icon-sm me-2 text-white" />
              Search
            </button>
          </div>
        </div>

        <div className="partners d-flex flex-column text-center">
          <p id="message" className="m-0">
            {!hideCarousel && (
              <strong>
                Studiosity is a trusted service, provided to you by your institution.
              </strong>
            )}
            {hideCarousel && currentName && (
              <Fragment>
                We found <b>{totalCount}</b> partner institution(s) relating to <b>"{currentName}"</b>
              </Fragment>
            )}
            {hideCarousel && !currentName && (
              <Fragment>
                We found <b>{totalCount}</b> partner institution(s)
              </Fragment>
            )}
          </p>

          {!hideCarousel && !isLoading && <PartnerCarousel partners={partners} />}

          {hideCarousel && (
            <Fragment>
              {isLoading && (
                <div className="loading d-flex align-items-center justify-content-center w-100">
                  <SvgIcon icon="spinner" className="icon-spin icon-md" />
                </div>
              )}

              {!isLoading && (
                <PartnerList
                  partners={currentPartners}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loadedImages={loadedImages}
                  onPageChange={setCurrentPage}
                  onImageLoad={handleImageLoad}
                />
              )}

              <div className="no-results mx-auto mb-4">
                <div className="d-flex flex-column align-items-center justify-content-center gap-3
                  border border-1 rounded-2 p-4"
                >
                  <h4 className="m-0">
                    <strong>
                      Can't see your institution?
                    </strong>
                  </h4>
                  <p id="message-end" className="m-0">
                    If your university, TAFE or college is not listed,
                    you do not have access yet or your institution may still be settings things up.
                    Check back later, or speak with your Student Services team.
                  </p>
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </section>
    </div>
  );
};

renderReact(AccessPage, 'access-page');

export default AccessPage;
